import React from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Ambient } from '../Ambient';
import { Reveal } from '../Reveal';
import { VexuAppPreview } from '../previews/VexuAppPreview';
import { PortalPreview } from '../previews/PortalPreview';
import { TalabPreview } from '../previews/TalabPreview';
import { openExternal, VEXU_PORTAL_URL } from '../../../data/presentationLinks';
interface VerticalsSlideProps {
  onOpenVexuApp: () => void;
  onOpenTalab: () => void;
}
export function VerticalsSlide({
  onOpenVexuApp,
  onOpenTalab
}: VerticalsSlideProps) {
  const products = [{
    index: '01',
    name: 'Vexu AI App',
    subtitle: 'AI Assistant for Every Phone Number',
    body: 'A consumer AI that answers, understands and acts on every call — on the number people already have.',
    cta: 'Explore Vexu AI App',
    external: false,
    patentPending: true,
    preview: <VexuAppPreview data-id="element-723" data-mp="35ej7ax33089y" />,
    onActivate: onOpenVexuApp
  }, {
    index: '02',
    name: 'Vexu Portal',
    subtitle: 'AI Communications for Businesses',
    body: 'A plug-and-play conversational AI platform for business communications.',
    cta: 'Launch Vexu Portal',
    external: true,
    patentPending: false,
    preview: <PortalPreview data-id="element-724" data-mp="rst10su43dc0" />,
    onActivate: () => openExternal(VEXU_PORTAL_URL)
  }, {
    index: '03',
    name: 'Talab',
    subtitle: 'Conversational E-Commerce',
    body: 'Transforming product discovery and commerce from search and filters into intelligent conversations.',
    cta: 'Explore Talab',
    external: false,
    patentPending: false,
    preview: <TalabPreview data-id="element-725" data-mp="1akvmchhp7hsl" />,
    onActivate: onOpenTalab
  }];
  return <section className="relative h-full w-full" data-id="element-726" data-mp="1fjp0mc4t7ctt">
      <Ambient variant="quiet" data-id="element-727" data-mp="1fmkw7tt6ppf3" />

      <div className="relative flex h-full flex-col px-[80px] pb-[42px] pt-[54px]" data-id="element-728" data-mp="3vb4jwdfvlw0d">
        <Reveal delay={0} data-id="element-729" data-mp="45bdj9hvr1eu">
          <div className="flex items-end justify-between" data-id="element-730" data-mp="45l5h2mhjin6">
            <h2 className="text-[40px] font-semibold tracking-[-0.03em] text-slate-900" data-id="element-731" data-mp="42ysuokqmcy3">
              One Technology. Three Commercial Verticals.
            </h2>
            <p className="pb-[6px] text-right text-[13.5px] font-light leading-[1.6] text-slate-400" data-id="element-732" data-mp="21tf9hj6e3rd4">
              The products are different.
              <br data-id="element-733" data-mp="1s3dm6tlv2cz9" />
              <span className="text-slate-700" data-id="element-734" data-mp="1s3vr0eml9zis">
                The technology underneath them is the same.
              </span>
            </p>
          </div>
        </Reveal>

        <div className="mt-[34px] grid flex-1 grid-cols-3 gap-[26px]" data-id="element-735" data-mp="1j2fiydj8y9hq">
          {products.map((product, index) => <Reveal key={product.name} delay={0.1 + index * 0.08} className="h-full" data-id="element-736" data-mp="1e2t5c2ziar8e" data-mpi={index}>
              <button type="button" onClick={product.onActivate} aria-label={product.cta} className="group flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-900/[0.07] bg-white text-left shadow-[0_1px_2px_rgba(15,23,42,0.04),0_18px_40px_-24px_rgba(15,23,42,0.18)] transition-[transform,border-color,box-shadow] duration-200 ease-out hover:-translate-y-[5px] hover:border-vexu/45 hover:shadow-[0_1px_2px_rgba(15,23,42,0.04),0_28px_54px_-24px_rgba(0,200,83,0.32)] focus:outline-none focus-visible:border-vexu focus-visible:ring-2 focus-visible:ring-vexu/40" data-id="element-737" data-mp="1962ohdfvnrlx">
                <div className="relative h-[268px] overflow-hidden border-b border-slate-900/[0.06] bg-gradient-to-b from-[#F4F8F6] to-[#EDF3EF]" data-id="element-738" data-mp="2s3pjs5zktvod">
                  {product.preview}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[70px] bg-gradient-to-t from-white to-transparent" data-id="element-739" data-mp="320kf6g0jzfyf" />
                </div>

                <div className="flex flex-1 flex-col px-[28px] pb-[26px] pt-[24px]" data-id="element-740" data-mp="36y3rhadoxinq">
                  <div className="flex items-baseline gap-[14px]" data-id="element-741" data-mp="hacz0rybypqy">
                    <span className="text-[11px] font-semibold tabular-nums tracking-[0.24em] text-vexu-600" data-id="element-742" data-mp="m96akeu246oh">
                      {product.index}
                    </span>
                    <h3 className="text-[26px] font-semibold tracking-[-0.025em] text-slate-900" data-id="element-743" data-mp="ftz1jjvc8qfk">
                      {product.name}
                    </h3>
                    {product.patentPending && <span className="rounded-full border border-vexu/35 bg-vexu/[0.07] px-[9px] py-[3px] text-[8.5px] font-semibold uppercase tracking-[0.18em] text-vexu-700" data-id="element-744" data-mp="otuhs33r3bmg">
                        Patent Pending
                      </span>}
                  </div>
                  <p className="mt-[10px] text-[15px] font-medium tracking-[-0.01em] text-slate-700" data-id="element-745" data-mp="1cjotuig8ww5n">
                    {product.subtitle}
                  </p>
                  <p className="mt-[10px] h-[64px] text-[13.5px] font-light leading-[1.6] text-slate-500" data-id="element-746" data-mp="17ln97t368os2">
                    {product.body}
                  </p>

                  <div className="mt-auto flex items-center gap-[9px] pt-[8px] text-[14px] font-medium text-vexu-600" data-id="element-747" data-mp="1hk4594gyvtkz">
                    <span data-id="element-748" data-mp="215cqp6ptbijq">{product.cta}</span>
                    {product.external ? <ArrowUpRight className="h-[16px] w-[16px] transition-transform duration-200 ease-out group-hover:translate-x-[3px] group-hover:-translate-y-[2px]" data-id="element-749" data-mp="25556a3dmioyl" /> : <ArrowRight className="h-[16px] w-[16px] transition-transform duration-200 ease-out group-hover:translate-x-[4px]" data-id="element-750" data-mp="1w4gmes1jtuds" />}
                  </div>
                </div>
              </button>
            </Reveal>)}
        </div>

        {/* Convergence back to the core */}
        <Reveal delay={0.4} data-id="element-751" data-mp="18i0f1rl6wv57">
          <div className="relative mt-[26px] h-[26px]" data-id="element-752" data-mp="1dfa3b3wuf0rg">
            <div className="absolute left-[16.6%] top-0 h-[13px] w-px bg-slate-300" data-id="element-753" data-mp="1dfs66je0gd8c" />
            <div className="absolute left-1/2 top-0 h-[13px] w-px bg-slate-300" data-id="element-754" data-mp="yjawrxpdt8of" />
            <div className="absolute left-[83.3%] top-0 h-[13px] w-px bg-slate-300" data-id="element-755" data-mp="13hceofb2n1yb" />
            <div className="absolute left-[16.6%] right-[16.6%] top-[13px] h-px bg-slate-300" data-id="element-756" data-mp="kczic7lcvsaj" />
            <div className="absolute left-1/2 top-[13px] h-[13px] w-px bg-slate-300" data-id="element-757" data-mp="fm0ywz52me32" />
          </div>
          <div className="flex justify-center" data-id="element-758" data-mp="am4vb10acoo1">
            <span className="rounded-full border border-vexu/40 bg-white px-[22px] py-[9px] text-[11.5px] font-semibold uppercase tracking-[0.26em] text-slate-900 shadow-[0_10px_28px_-14px_rgba(0,200,83,0.5)]" data-id="element-759" data-mp="amxthdl6i5uk">
              Vexu AI Core
            </span>
          </div>
        </Reveal>
      </div>
    </section>;
}